import { bootFromHash } from '@devhacker/shared/features/auth/utils/authRedirect';
import { shouldOpenProfileSetup } from '@devhacker/shared/features/auth/utils/shouldOpenProfileSetup';
import type { EmailPolicy } from '@devhacker/shared/server/bff/settingsSection';
import { useSystemPush } from '@devhacker/shared/features/system-push/useSystemPush';
import { requestEmailChange, resendEmailConfirmation } from '@devhacker/shared/features/auth/api';
import { patchProfileUser } from '@devhacker/shared/features/profile/api';
import {
  clearUserToken,
  getAccessToken,
  getSubId,
  isValidAccessToken,
  setAccessToken,
} from '@devhacker/shared/features/auth/utils/token';
import { refreshTokenSingleFlight } from '@devhacker/shared/features/auth/utils/refreshSingleFlight';
import { useAuthService } from '~/features/auth/service';
import { useUserService } from '~/features/user/service';
import { useUserStore } from '~/features/user/store';
import { isRequiresAuth } from '~/utils';

export default defineNuxtPlugin({
  dependsOn: ['pinia'],
  async setup() {
    function redirectIfProtected() {
      if (isRequiresAuth(useRoute())) location.href = '/';
    }

    /**
     * Политика email-доменов для попапа дозаполнения: домены, текст отказа и адрес
     * поддержки — всё из auth-sso через BFF.
     * Fail-open: при любой ошибке отдаём пустую политику — на фронте это означает
     * «домен не проверяем», блокировать сохранение профиля из-за настроек нельзя.
     * Таймаут обязателен: попап открывается ПОСЛЕ этого запроса, и без него
     * зависший ответ означает, что попап не покажется вообще.
     */
    async function fetchEmailPolicy(): Promise<EmailPolicy> {
      try {
        return await $fetch<EmailPolicy>('/bff/allowed-email-domains', {
          signal: AbortSignal.timeout(3000),
        });
      } catch (error) {
        console.error('failed to fetch allowed email domains', error);
        return { domains: [], message: '', support_email: '' };
      }
    }

    async function openProfileSetupIfNeeded() {
      const user = useUserStore().userData;
      if (!user) return;
      if (!shouldOpenProfileSetup(user)) return;

      const ownerId = user.integer_id;
      const emailPolicy = await fetchEmailPolicy();

      // Третья сущность очереди системных пушей: после cookie-баннера и пуша, cooldown 12 ч.
      useSystemPush().requestProfileSetup({
        id: ownerId,
        name: user.display_name || '',
        email: user.email || '',
        emailTemp: user.email_temp || '',
        emailPolicy,
        onResendEmail: () => resendEmailConfirmation(ownerId, { redirect_url: location.href }),
        onSubmit: async (payload: { display_name?: string; email?: string }) => {
          if (payload.email) {
            await requestEmailChange(ownerId, {
              email: payload.email,
              redirect_url: location.href,
            });
          }

          if (payload.display_name) {
            try {
              await patchProfileUser(ownerId, { display_name: payload.display_name });
            } catch (error) {
              if (payload.email) {
                console.error('profile patch failed after email change was accepted', error);
              }
              throw error;
            }
          }

          useUserService().patchUser({
            ...(payload.display_name ? { display_name: payload.display_name } : {}),
            ...(payload.email ? { email_temp: payload.email } : {}),
          });
        },
      });
    }

    async function loadUser() {
      await useAuthService().login();
      await openProfileSetupIfNeeded();
    }

    async function initialize() {
      bootFromHash();

      const accessToken = getAccessToken();
      const subId = accessToken ? getSubId(accessToken) : null;

      if (!accessToken || !subId) {
        redirectIfProtected();
        return;
      }

      if (isValidAccessToken(accessToken)) {
        await loadUser();
        return;
      }

      try {
        const result = await refreshTokenSingleFlight();

        if (!result.access_token) {
          clearUserToken();
          useUserService().clearUser();
          redirectIfProtected();
          return;
        }

        setAccessToken(result.access_token);
        await loadUser();
      } catch {
        redirectIfProtected();
      }
    }

    onNuxtReady(() => {
      initialize().catch((error) => console.error('user-plugin init', error));
    });
  },
});
